import type { CompanyViewModel, DetailCompanyViewModel } from "@vintecc/capture-api-types/v1";

import { getSubrows } from "../utils/helpers";
import { useApiToast } from "@/hooks/apiCall";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/queryClient";
import { useAppStore } from "@/store/appStore";
import company from "@/api/company.api";
import { apiDefaultParams } from "@/api/default.params";

const getDatabases = async () => (await import("@/api/database.api")).default;

export const requests = {
  key: "companies",
  create: async (companyView: CompanyViewModel) => {
    return await company.v1.createCompany(apiDefaultParams.authorization, apiDefaultParams.authVersion, apiDefaultParams.companyView, companyView);
  },
  switchCaptureCompany: async (companyName: string) => {
    return await company.v1.switchCaptureCompany(apiDefaultParams.apiVersion, apiDefaultParams.authorization, companyName);
  },
  read: async () => {
    const response = await company.v1.getAllCompaniesAsTable(apiDefaultParams.apiVersion);
    return getSubrows(response.data);
  },
  readSimple: async () => {
    return await company.v1.getAllCompaniesAsTable();
  },
  getCompanyById: async (companyId: number) => {
    const response = await company.v1.getCompanyById(companyId);
    return response.data;
  },
  getAllCompanies: async () => {
    const response = await company.v1.getAllCompanies(apiDefaultParams.authorization, apiDefaultParams.authVersion, apiDefaultParams.companyView);
    return response.data;
  },
  getAllCompaniesNameId: async () => {
    const response = await company.v1.getAllCompaniesNameId(
      apiDefaultParams.authorization,
      apiDefaultParams.authVersion,
      apiDefaultParams.companyView,
    );
    return response.data;
  },
  getCompanyAppsByCompanyId: async (companyId: number) => {
    return await company.v1.getCompanyAppsByCompanyId(companyId);
  },
  getCompanyTree: async (companyId: number) => {
    const response = await company.v2.getCompanyTree(companyId);
    return response.data;
  },
  update: async (companyId: number, companyView: CompanyViewModel) => {
    return await company.v1.companyEdit(
      companyId,
      apiDefaultParams.authorization,
      apiDefaultParams.authVersion,
      apiDefaultParams.companyView,
      companyView,
    );
  },
  editTags: async (companyId: number, tags: Record<string, Array<string>>) => {
    return await company.v1.setCompanyTags(
      companyId,
      apiDefaultParams.authorization,
      apiDefaultParams.authVersion,
      apiDefaultParams.companyView,
      tags,
    );
  },
  delete: async (companyId: number) => {
    return await company.v1.deleteCompany(companyId);
  },
};

export const databaseRequests = {
  key: "database",
  getDatabaseEndpoints: async () => {
    const databases = await getDatabases();
    const response = await databases.v1.data.getDatabaseEndpoints(
      apiDefaultParams.authorization,
      apiDefaultParams.authVersion,
      apiDefaultParams.companyView,
    );
    return response.data;
  },
};

export const queries = {
  getAllCompanies: queryOptions({
    queryKey: [requests.key, "all"],
    queryFn: requests.getAllCompanies,
  }),
  getCompanies: (companyName?: string) =>
    queryOptions({
      queryKey: [requests.key, "currentCompanyCompanies", companyName],
      queryFn: requests.read,
    }),
  getCompaniesList: queryOptions({
    queryKey: [requests.key, "list"],
    queryFn: requests.readSimple,
  }),
  getCompanyById: (companyId: number | undefined) =>
    queryOptions({
      queryKey: [requests.key, companyId],
      queryFn: async () => requests.getCompanyById(companyId!),
      enabled: !!companyId,
    }),
  getCompanyAppsByCompanyId: (companyId: number) =>
    queryOptions({
      queryKey: [requests.key, companyId, "apps"],
      queryFn: async () => requests.getCompanyAppsByCompanyId(companyId),
      enabled: !!companyId,
    }),
  getAllCompaniesNameId: queryOptions({
    queryKey: [requests.key, "nameId"],
    queryFn: async () => requests.getAllCompaniesNameId(),
  }),
  getCompanyTree: (companyId: number | undefined) =>
    queryOptions({
      queryKey: [requests.key, companyId, "tree"],
      queryFn: async () => requests.getCompanyTree(companyId!),
      enabled: !!companyId,
    }),
};

export const databaseQueries = {
  getDatabaseEndpoints: (isAdmin: boolean) =>
    queryOptions({
      queryKey: [databaseRequests.key],
      queryFn: databaseRequests.getDatabaseEndpoints,
      enabled: isAdmin,
    }),
};

export const useMutations = () => {
  const { onSuccess, onError } = useApiToast(requests.key);

  const createCompany = useMutation({
    mutationFn: requests.create,
    onSuccess,
    onError,
  });

  const updateCompany = useMutation({
    mutationFn: async ({ company, companyId }: { companyId: number; company: CompanyViewModel }) => requests.update(companyId, company),
    onSuccess,
    onError,
  });

  const editCompanyTags = useMutation({
    mutationFn: async ({ companyId, tags }: { companyId: number; tags: DetailCompanyViewModel["tags"] }) => requests.editTags(companyId, tags || {}),
    onSuccess,
    onError,
  });

  const deleteCompany = useMutation({
    mutationFn: requests.delete,
    onSuccess,
    onError,
  });

  const switchCaptureCompany = useMutation({
    mutationFn: async (companyName: string) => requests.switchCaptureCompany(companyName),
    onSuccess: (data, companyName) => {
      // Update the current company in the app store
      useAppStore.getState().setCurrentCompany(companyName);
      // Clear all queries to avoid data leakage between companies
      queryClient.clear();
      onSuccess(data);
    },
    onError,
  });

  return {
    createCompany,
    updateCompany,
    editCompanyTags,
    deleteCompany,
    switchCaptureCompany,
  };
};
